import React, { PureComponent, ReactNode } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { colors } from '@atlaskit/theme';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import Code, {
  CodeSegment,
  getLanguage,
  SyntaxHighlighting,
} from '@atlassian/bitkit-code';
import emphasizeMatches from '@atlassian/bitkit-code/src/utils/emphasize-matches';

import { Line } from 'src/types/search';
import { publishFact, publishUiEvent } from 'src/utils/analytics/publish';

import generateCodeLineHash from '../../../../utils/code-line-url-hash';
import { SearchResultsSourceFileLineClickedFact } from '../../facts';
import getMatchRanges from '../../utils/get-match-ranges';
import transformSearchResultSourceLines from '../../utils/transform-search-result-source-lines';

import * as styles from './hunk.style';

const StyledCode = styled(Code)<any>`
  ${styles.hunkPadding};
  border-top: 1px solid ${colors.N40};
`;

const renderSourceLine = (source: ReactNode) => (
  <styles.SearchSourceCodeLine>
    <styles.SearchSourceCodeText>{source}</styles.SearchSourceCodeText>
  </styles.SearchSourceCodeLine>
);

export type HunkProps = {
  filename: string;
  lines: Line[];
  srcHref?: string;
  pageNum: number;
  resultNum: number;
  accountUuid: string;
};

export default class Hunk extends PureComponent<HunkProps> {
  generateLineKey = (_source: ReactNode, idx: number) => {
    return this.props.lines[idx].line.toString();
  };

  renderGutterLine = (idx: number) => {
    const { srcHref, pageNum, resultNum } = this.props;
    const { line: num } = this.props.lines[idx];

    // If line number is unknown, leave the gutter empty
    if (!num) {
      return <styles.Gutter />;
    }

    const hash = generateCodeLineHash(this.props.filename, [num]);
    const fragment = hash ? `#${hash}` : '';
    const href = srcHref ? `${srcHref}${fragment}` : undefined;

    return (
      <styles.Gutter>
        {href ? (
          <styles.SubtleLink
            onClick={() => {
              publishFact(
                new SearchResultsSourceFileLineClickedFact({
                  line_num: num,
                  result_num: resultNum,
                  page_num: pageNum,
                })
              );
              publishUiEvent({
                action: 'clicked',
                actionSubject: 'link',
                actionSubjectId: 'sourceFileLineLink',
                source: 'search',
                containerType: 'account',
                containerId: uncurlyUuid(this.props.accountUuid),
                attributes: {
                  line_num: num,
                  result_num: resultNum,
                  page_num: pageNum,
                },
              });
            }}
            href={href}
          >
            {num}
          </styles.SubtleLink>
        ) : (
          num
        )}
      </styles.Gutter>
    );
  };

  render() {
    const hunkRenderProps = {
      generateLineKey: this.generateLineKey,
      renderGutterLine: this.renderGutterLine,
      renderSourceLine,
    };

    const matchPositions = this.props.lines.map(l =>
      getMatchRanges(l.segments)
    );
    const language = getLanguage(this.props.filename);

    return (
      <SyntaxHighlighting
        language={language ? language.prism : undefined}
        source={transformSearchResultSourceLines(this.props.lines)}
      >
        {(lines: CodeSegment[]) => (
          <StyledCode
            sourceLines={lines.map((l, i) =>
              emphasizeMatches(l, matchPositions[i])
            )}
            {...hunkRenderProps}
          />
        )}
      </SyntaxHighlighting>
    );
  }
}
